import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import {
  IconUsers,
  IconUserCheck,
  IconUserPlus,
  IconSend,
  IconCircleCheck,
  IconAlertCircle,
  IconLoader2,
  IconRefresh,
  IconTrash,
  IconIdBadge,
} from "@tabler/icons-react";
import { toast } from "sonner";
import { KpiCard } from "@/components/data/KpiCard";
import {
  listBrokers,
  getSeats,
  resendInvite,
  revokeInvite,
  activateLicense,
} from "../api";
import { useIdempotencyKey } from "@/hooks/useIdempotencyKey";
import { InviteBrokerModal } from "../components/InviteBrokerModal";

export function BrokersPage() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [isInviteOpen, setIsInviteOpen] = useState(false);

  const { data: brokers, isLoading: isBrokersLoading } = useQuery({
    queryKey: ["agency-brokers-list"],
    queryFn: listBrokers,
  });

  const { data: seats, isLoading: isSeatsLoading } = useQuery({
    queryKey: ["agency-seats"],
    queryFn: getSeats,
  });

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ["agency-brokers-list"] });
    queryClient.invalidateQueries({ queryKey: ["agency-seats"] });
  };

  const resendMutation = useMutation({
    mutationFn: resendInvite,
    onSuccess: () => {
      toast.success(t("brokers.toast.resent"));
      invalidate();
    },
    onError: () => toast.error(t("brokers.toast.resendError")),
  });

  const revokeMutation = useMutation({
    mutationFn: revokeInvite,
    onSuccess: () => {
      toast.success(t("brokers.toast.revoked"));
      invalidate();
    },
    onError: () => toast.error(t("brokers.toast.revokeError")),
  });

  // Ativar licença sincroniza o seat com o billing: repetir depois de uma falha de rede
  // sincronizaria duas vezes. A chave mantém a tentativa sendo a mesma operação.
  const { key: activateKey, rotate: rotateActivateKey } = useIdempotencyKey();

  const activateMutation = useMutation({
    mutationFn: (brokerId: string) => activateLicense(brokerId, activateKey()),
    onSuccess: () => {
      rotateActivateKey();
      toast.success(t("brokers.toast.activated"));
      invalidate();
    },
    onError: () => toast.error(t("brokers.toast.activateError")),
  });

  if (isBrokersLoading || isSeatsLoading || !brokers || !seats) {
    return (
      <div className="p-8 text-center text-ink-soft font-bold italic animate-pulse">
        {t("brokers.loading")}
      </div>
    );
  }

  const noSeats = seats.availableSlots <= 0;

  return (
    <div className="space-y-8">
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div className="flex flex-col gap-1">
          <h2 className="text-3xl font-black tracking-tight text-ink">
            {t("brokers.title")}
          </h2>
          <p className="text-ink-soft font-medium">{t("brokers.subtitle")}</p>
        </div>

        <button
          onClick={() => setIsInviteOpen(true)}
          disabled={noSeats}
          title={noSeats ? t("brokers.noSeatsTitle") : undefined}
          className="inline-flex items-center justify-center gap-2 rounded-2xl bg-brand-grad px-6 py-3 text-sm font-black uppercase tracking-wider text-white shadow-glow transition-all hover:scale-105 active:scale-95 disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:scale-100"
        >
          <IconSend size={18} />
          {t("brokers.invite")}
        </button>
      </div>

      {/* SEATS DO PLANO */}
      <div className="grid gap-6 md:grid-cols-3">
        <KpiCard
          label={t("brokers.seats.total")}
          value={seats.totalSlots}
          icon={<IconUsers size={24} />}
          tint="info"
          description={t("brokers.seats.totalDesc", { plan: seats.planName })}
        />
        <KpiCard
          label={t("brokers.seats.used")}
          value={seats.usedSlots}
          icon={<IconUserCheck size={24} />}
          tint="success"
        />
        <KpiCard
          label={t("brokers.seats.available")}
          value={seats.availableSlots}
          icon={<IconUserPlus size={24} />}
          tint={noSeats ? "warning" : "brand"}
          description={
            noSeats
              ? t("brokers.seats.availableFull")
              : t("brokers.seats.availableReady")
          }
        />
      </div>

      {noSeats && (
        <div className="flex items-center gap-3 rounded-2xl border border-amber-200 bg-amber-50 px-5 py-4 text-amber-700">
          <IconAlertCircle size={20} className="shrink-0" />
          <p className="text-sm font-bold">
            {t("brokers.seats.fullBanner", { plan: seats.planName })}
          </p>
        </div>
      )}

      {/* ROSTER */}
      <div className="rounded-3xl border border-white/40 bg-white/70 shadow-card backdrop-blur-sm overflow-hidden animate-in fade-in slide-in-from-bottom-4 duration-700">
        <div className="border-b border-border-soft bg-gray-50/30 px-8 py-5">
          <h3 className="font-black text-ink">{t("brokers.roster.title")}</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead className="border-b border-border-soft bg-gray-50/50 text-[11px] font-black uppercase tracking-widest text-ink-soft">
              <tr>
                <th className="px-8 py-4">{t("brokers.roster.colBroker")}</th>
                <th className="px-8 py-4">{t("brokers.roster.colEmail")}</th>
                <th className="px-8 py-4">{t("brokers.roster.colStatus")}</th>
                <th className="px-8 py-4 text-right">
                  {t("brokers.roster.colActions")}
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border-soft">
              {brokers.map((broker) => {
                const isInvited = broker.status === "invited";
                const isPendingLicense = broker.status === "pending_license";
                const isBusy =
                  (resendMutation.isPending &&
                    resendMutation.variables === broker.id) ||
                  (revokeMutation.isPending &&
                    revokeMutation.variables === broker.id) ||
                  (activateMutation.isPending &&
                    activateMutation.variables === broker.id);

                return (
                  <tr
                    key={broker.id}
                    className="group hover:bg-white/50 transition-colors"
                  >
                    <td className="px-8 py-5">
                      <div className="flex items-center gap-3">
                        <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-brand-50 text-brand-700 font-black text-xs shadow-inner-light ring-2 ring-white">
                          {broker.name.substring(0, 2).toUpperCase()}
                        </div>
                        <div className="flex flex-col leading-tight">
                          <span className="font-bold text-ink">{broker.name}</span>
                          <span className="text-[10px] uppercase font-black tracking-wider text-ink-soft">
                            {broker.linkType
                              ? t(`brokers.linkType.${broker.linkType}`)
                              : t("brokers.roster.role")}
                          </span>
                        </div>
                      </div>
                    </td>
                    <td className="px-8 py-5 font-medium text-ink-muted">
                      {broker.email}
                    </td>
                    <td className="px-8 py-5">
                      {broker.status === "active" ? (
                        <span className="inline-flex items-center gap-1.5 rounded-full bg-success-soft px-3 py-1 text-[11px] font-black uppercase tracking-wider text-success">
                          <IconCircleCheck size={14} />{" "}
                          {t("brokers.status.active")}
                        </span>
                      ) : broker.status === "pending_license" ? (
                        <span className="inline-flex items-center gap-1.5 rounded-full bg-warning-soft px-3 py-1 text-[11px] font-black uppercase tracking-wider text-warning">
                          <IconAlertCircle size={14} />{" "}
                          {t("brokers.status.pending_license")}
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1.5 rounded-full bg-info-soft px-3 py-1 text-[11px] font-black uppercase tracking-wider text-info">
                          <IconSend size={14} /> {t("brokers.status.invited")}
                        </span>
                      )}
                    </td>
                    <td className="px-8 py-5">
                      <div className="flex items-center justify-end gap-2">
                        {isInvited ? (
                          <>
                            <button
                              onClick={() => resendMutation.mutate(broker.id)}
                              disabled={isBusy}
                              className="inline-flex items-center gap-1.5 rounded-xl border border-border bg-white px-3 py-2 text-[11px] font-black uppercase tracking-wider text-ink-muted transition-all hover:border-brand-300 hover:text-brand-600 disabled:opacity-50"
                            >
                              {resendMutation.isPending &&
                              resendMutation.variables === broker.id ? (
                                <IconLoader2 size={14} className="animate-spin" />
                              ) : (
                                <IconRefresh size={14} />
                              )}
                              {t("brokers.actions.resend")}
                            </button>
                            <button
                              onClick={() => revokeMutation.mutate(broker.id)}
                              disabled={isBusy}
                              className="inline-flex items-center gap-1.5 rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-[11px] font-black uppercase tracking-wider text-red-600 transition-all hover:bg-red-100 disabled:opacity-50"
                            >
                              {revokeMutation.isPending &&
                              revokeMutation.variables === broker.id ? (
                                <IconLoader2 size={14} className="animate-spin" />
                              ) : (
                                <IconTrash size={14} />
                              )}
                              {t("brokers.actions.revoke")}
                            </button>
                          </>
                        ) : isPendingLicense ? (
                          <button
                            onClick={() => activateMutation.mutate(broker.id)}
                            disabled={isBusy}
                            className="inline-flex items-center gap-1.5 rounded-xl bg-brand-grad px-4 py-2 text-[11px] font-black uppercase tracking-wider text-white shadow-soft transition-all hover:scale-105 hover:shadow-glow active:scale-95 disabled:opacity-50 disabled:hover:scale-100"
                          >
                            {activateMutation.isPending &&
                            activateMutation.variables === broker.id ? (
                              <IconLoader2 size={14} className="animate-spin" />
                            ) : (
                              <IconIdBadge size={14} />
                            )}
                            {t("brokers.actions.activate")}
                          </button>
                        ) : (
                          <span className="text-[11px] font-black uppercase tracking-widest text-success opacity-50">
                            {t("brokers.actions.inTeam")}
                          </span>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <InviteBrokerModal open={isInviteOpen} onOpenChange={setIsInviteOpen} />
    </div>
  );
}
